import { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { BookOpen, CheckCircle, Clock, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ContentExtrasBar } from '@/components/content/ContentExtrasBar';
import DOMPurify from 'dompurify';

interface ReadingContentModalProps {
  isOpen: boolean;
  contentId?: string;
  contentDescription?: string | null;
  onClose: () => void;
  lesson: {
    title: string;
    description: string;
    duration: string;
  };
  htmlContent?: string | null;
  onComplete: () => void;
  isCompleted: boolean;
}

export function ReadingContentModal({
  isOpen,
  contentId,
  contentDescription,
  onClose,
  lesson,
  htmlContent,
  onComplete,
  isCompleted,
}: ReadingContentModalProps) {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight - target.clientHeight;
    if (scrollHeight <= 0) {
      setScrollProgress(100);
      setReachedEnd(true);
      return;
    }
    const percent = Math.round((scrollTop / scrollHeight) * 100);
    setScrollProgress(percent);
    if (percent >= 90) setReachedEnd(true);
  }, []);

  const sanitizedHtml = htmlContent ? DOMPurify.sanitize(htmlContent) : null;

  const handleClose = () => {
    setIsFullscreen(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className={cn(
        "flex flex-col p-0 gap-0",
        isFullscreen
          ? "max-w-[100vw] w-screen h-screen max-h-screen rounded-none"
          : "max-w-3xl max-h-[90vh]"
      )}>
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="secondary" className="gap-1">
                <BookOpen className="w-3 h-3" />
                Lectura
              </Badge>
              <Badge variant="outline" className="gap-1">
                <Clock className="w-3 h-3" />
                {lesson.duration}
              </Badge>
              {isCompleted && (
                <Badge className="bg-success text-success-foreground gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Completado
                </Badge>
              )}
            </div>
            <button
              onClick={() => setIsFullscreen(f => !f)}
              className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
              title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4 text-muted-foreground" /> : <Maximize2 className="w-4 h-4 text-muted-foreground" />}
            </button>
          </div>
          <DialogTitle className="text-xl mt-2">{lesson.title}</DialogTitle>
          {lesson.description && (
            <p className="text-sm text-muted-foreground mt-1">{lesson.description}</p>
          )}
          {/* Scroll progress */}
          <div className="mt-3">
            <Progress value={scrollProgress} className="h-1.5" />
          </div>
        </DialogHeader>
        {contentId && <ContentExtrasBar contentId={contentId} description={contentDescription ?? lesson?.description} />}

        {/* Content body */}
        <div
          className="flex-1 overflow-y-auto px-6 py-6"
          onScroll={handleScroll}
          ref={contentRef}
        >
          {sanitizedHtml ? (
            <div
              className="prose prose-sm dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          ) : (
            <div className="text-muted-foreground text-center py-12">
              <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay contenido de lectura disponible para esta lección.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex-shrink-0 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {reachedEnd || isCompleted ? 'Lectura completa' : 'Desplázate para leer todo el contenido'}
          </span>
          {!isCompleted ? (
            <Button
              onClick={onComplete}
              disabled={!reachedEnd}
              className="gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Marcar como completado
            </Button>
          ) : (
            <Button variant="outline" onClick={handleClose}>
              Cerrar
            </Button>
          )}
        </div>
        </DialogContent>
    </Dialog>
  );
}